import axios from "axios"

const instance = axios.create({
 baseURL: "https://taaouniaty-backend.onrender.com",
 //baseURL:"http://localhost:8000",
})

const publicRoutes = [
  "/api/auth/register/",
  "/api/auth/login/",
  "/api/auth/token/refresh/",
  "/api/auth/refresh/",
  "/api/marketplace/coop/",
  "/api/marketplace/reviews/",
  "/api/marketplace/receipt/",
  "/api/blog/posts/",
  "/api/blog/banners/",
  "/api/articles/cooperatives/",
  "/api/shop/",
  "/api/shop/orders/",
  "/api/shop/auth/",
]

// Flag to prevent multiple refresh requests
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

instance.interceptors.request.use((config) => {
    const url = config.url || ""
    const isReviewsManage = url.includes("/api/marketplace/reviews/manage/")
    const isShopAdmin = url.includes("/api/shop/admin/")
    const isShopMe = url.includes("/api/shop/me/")
    const isShopAuth = url.includes("/api/shop/auth/")
    const shopToken = localStorage.getItem("taa-shop-token")
    if ((isShopMe || url.includes("/api/shop/orders/")) && shopToken) {
      config.headers.Authorization = `Shop ${shopToken}`
    }
    if (isShopMe || isShopAuth) {
      return config
    }
    const isPublic =
      !isReviewsManage &&
      !isShopAdmin &&
      publicRoutes.some((route) => url.includes(route))
    const token = localStorage.getItem("access_token")
  
    if (!isPublic && token) {
      config.headers.Authorization = `Bearer ${token}`
    } else if (isPublic && config.headers) {
      // A stale token from another server must not block public login or signup.
      delete config.headers.Authorization
    }
    
    // Don't redirect on feature flags endpoint - let it fail gracefully
    if (url.includes('/api/user-plan-features/')) {
      config.validateStatus = (status) => status < 500;
    }
  
    return config
  })

instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config
    if (!originalRequest) return Promise.reject(error)

    // Handle trial expiration
    if (error.response && error.response.status === 403 && error.response.data && error.response.data.trial_expired) {
      // Trial expired - redirect to plans page or show upgrade modal
      window.dispatchEvent(new CustomEvent("trialExpired", { 
        detail: error.response.data 
      }));
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      const failedUrl = originalRequest.url || ""
      if (
        failedUrl.includes("/api/shop/me/") ||
        failedUrl.includes("/api/shop/auth/") ||
        failedUrl.includes("/api/auth/login/") ||
        failedUrl.includes("/api/auth/register/") ||
        failedUrl.includes("/api/auth/refresh/")
      ) {
        return Promise.reject(error)
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return instance(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = localStorage.getItem("refresh_token")
      
      if (!refreshToken) {
        // No refresh token, log out
        localStorage.removeItem("access_token")
        localStorage.removeItem("refresh_token")
        delete instance.defaults.headers.common.Authorization
        window.dispatchEvent(new CustomEvent("sessionExpired"))
        const path = window.location.pathname || '/'
        const isPublicPage =
          path === '/' ||
          path.startsWith('/blog') ||
          path.startsWith('/article') ||
          path.startsWith('/marketplace') ||
          path.startsWith('/care-dashboard-demo') ||
          (!path.startsWith('/dashboard') &&
            !path.startsWith('/login') &&
            !path.startsWith('/register') &&
            !path.startsWith('/complete-profile') &&
            !path.startsWith('/onboarding') &&
            !path.startsWith('/admin') &&
            !path.startsWith('/pos') &&
            path.split('/').filter(Boolean).length === 1)
        if (!isPublicPage && path !== '/login' && path !== '/register') {
          window.location.href = "/login"
        }
        isRefreshing = false
        return Promise.reject(error)
      }

      try {
        const base = (instance.defaults.baseURL || "").replace(/\/$/, "")
        const response = await axios.post(`${base}/api/auth/refresh/`, {
          refresh: refreshToken
        })

        const { access } = response.data
        localStorage.setItem("access_token", access)
        instance.defaults.headers.common['Authorization'] = `Bearer ${access}`
        originalRequest.headers.Authorization = `Bearer ${access}`
        
        processQueue(null, access)
        isRefreshing = false

        return instance(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        isRefreshing = false
        
        // Refresh token is invalid, log out
        localStorage.removeItem("access_token")
        localStorage.removeItem("refresh_token")
        delete instance.defaults.headers.common.Authorization
        window.dispatchEvent(new CustomEvent("sessionExpired"))
        const path = window.location.pathname || '/'
        const isPublicPage =
          path === '/' ||
          path.startsWith('/blog') ||
          path.startsWith('/article') ||
          path.startsWith('/marketplace') ||
          path.startsWith('/care-dashboard-demo') ||
          (!path.startsWith('/dashboard') &&
            !path.startsWith('/login') &&
            !path.startsWith('/register') &&
            !path.startsWith('/complete-profile') &&
            !path.startsWith('/onboarding') &&
            !path.startsWith('/admin') &&
            !path.startsWith('/pos') &&
            path.split('/').filter(Boolean).length === 1)
        if (!isPublicPage && path !== '/login' && path !== '/register') {
          window.location.href = "/login"
        }
        return Promise.reject(refreshError)
      }
    }
    
    return Promise.reject(error)
  }
);
  
export default instance
export const axiosInstance = instance

// Utility: Get currency label based on currency and language
export function getCurrencyLabel(currency, language) {
  if (currency === 'MAD') {
    return language === 'ar' ? 'درهم' : 'MAD';
  }
  return currency;
}
